import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '@matic/lib/utils/cn';
import {Spinner} from './spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-sidebar-bg text-white hover:bg-sidebar-hover hover:text-primary-400 rounded-lg shadow-md',
        dark: 'bg-sidebar-bg text-white hover:bg-sidebar-hover hover:text-primary-400 rounded-lg shadow-md',
        secondary: 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200',
        destructive: 'bg-error-600 text-white hover:bg-error-700',
        warning:
          'bg-orange-500 text-white hover:text-black hover:bg-orange-500',
        outline:
          'border border-neutral-300 bg-transparent text-neutral-900 hover:bg-neutral-100',
        ghost: 'hover:bg-neutral-100 text-neutral-900',
      },
      size: {
        sm: 'px-3 py-1.5 text-sm h-8',
        md: 'px-4 py-2 h-10',
        lg: 'px-6 py-3 text-base h-12',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export type ButtonProps = {
  loading?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {className, variant, size, loading = false, disabled, children, ...props},
    ref
  ) => {
    return (
      <button
        className={cn(buttonVariants({variant, size, className}))}
        ref={ref}
        disabled={disabled ?? loading}
        aria-disabled={disabled ?? loading}
        {...props}
      >
        {loading && <Spinner size="sm" className="mr-2" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export {Button, buttonVariants};
export default Button;
